import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  IntegrationTokenDto,
  IntegrationWebhookDeliveryStatus,
} from '@aiai/shared';
import {
  clearIntegrationWebhook,
  listIntegrationWebhookDeliveries,
  rotateIntegrationWebhookSecret,
  setIntegrationWebhook,
} from '../api';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { Callout } from './Callout';
import { Spinner } from './Spinner';

const DELIVERY_TONE: Record<IntegrationWebhookDeliveryStatus, string> = {
  delivered: 'text-success',
  retrying: 'text-warning',
  failed: 'text-danger',
  skipped: 'text-ink-faint',
};

/** The signing secret, shown once with a copy affordance. */
function SecretBanner({ secret, onDone }: { secret: string; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="rounded border border-signal/40 bg-signal/5 p-3">
      <p className="mb-2 text-xs font-medium text-ink">
        Signing secret. Copy it now: it is shown only once. Verify each delivery's{' '}
        <code>X-Aiai-Signature</code> (<code>sha256=</code> HMAC of the raw body) with it.
      </p>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <code className="flex-1 select-all break-all rounded bg-surface px-2 py-1.5 font-mono text-xs text-ink">
          {secret}
        </code>
        <button
          type="button"
          className="btn-secondary whitespace-nowrap"
          onClick={() => {
            void navigator.clipboard?.writeText(secret);
            setCopied(true);
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
        <button type="button" className="btn-primary whitespace-nowrap" onClick={onDone}>
          Done
        </button>
      </div>
    </div>
  );
}

/**
 * Owner-only: one integration token's status-change webhook — set the https
 * URL, reveal/rotate the signing secret once, remove it, and read the recent
 * delivery log (status, attempt, HTTP code; never payloads). `projectId` null =
 * a workspace token (prototype factory).
 */
export function IntegrationWebhookPanel({
  projectId,
  token,
}: {
  projectId: string | null;
  token: IntegrationTokenDto;
}) {
  const queryClient = useQueryClient();
  const [url, setUrl] = useState(token.webhookUrl ?? '');
  const [secret, setSecret] = useState<string | null>(null);

  const deliveriesKey = qk.integrationWebhookDeliveries(projectId, token.id);
  const deliveries = useQuery({
    queryKey: deliveriesKey,
    queryFn: () => listIntegrationWebhookDeliveries(projectId, token.id),
    enabled: !!token.webhookUrl,
    refetchInterval: 15_000,
  });
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: qk.integrationTokens(projectId) });

  const save = useMutation({
    mutationFn: () => setIntegrationWebhook(projectId, token.id, url.trim()),
    onSuccess: (res) => {
      if (res.secret) setSecret(res.secret);
      if (res.webhookUrl) setUrl(res.webhookUrl);
      invalidate();
    },
  });
  const rotate = useMutation({
    mutationFn: () => rotateIntegrationWebhookSecret(projectId, token.id),
    onSuccess: (res) => setSecret(res.secret),
  });
  const remove = useMutation({
    mutationFn: () => clearIntegrationWebhook(projectId, token.id),
    onSuccess: () => {
      setUrl('');
      setSecret(null);
      invalidate();
    },
  });
  const failure = save.error ?? rotate.error ?? remove.error;

  return (
    <div className="space-y-3 border-t border-border bg-surface/40 px-3 py-3">
      <p className="text-xs text-ink-muted">
        {projectId === null ? (
          <>
            POST the prototype's status (<code>prototype.status_changed</code>) to your server on
            every change, signed with HMAC-SHA256.
          </>
        ) : (
          <>POST the request status to your server on every change, signed with HMAC-SHA256.</>
        )}{' '}
        The URL must be https and publicly reachable; retries back off for about 15 minutes.
      </p>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <input
          type="url"
          className="input flex-1"
          value={url}
          maxLength={2048}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.com/hooks/aiai"
          aria-label={`Webhook URL for ${token.name}`}
          autoComplete="off"
          spellCheck={false}
        />
        <button
          type="button"
          className="btn-primary whitespace-nowrap"
          disabled={save.isPending || !url.trim() || url.trim() === token.webhookUrl}
          onClick={() => save.mutate()}
        >
          {save.isPending && <Spinner className="h-3.5 w-3.5" />}
          Save webhook
        </button>
        {token.webhookUrl && (
          <>
            <button
              type="button"
              className="btn-secondary whitespace-nowrap"
              disabled={rotate.isPending}
              onClick={() => rotate.mutate()}
            >
              Rotate secret
            </button>
            <button
              type="button"
              className="btn-danger-ghost whitespace-nowrap"
              disabled={remove.isPending}
              onClick={() => remove.mutate()}
            >
              Remove
            </button>
          </>
        )}
      </div>
      {failure && <Callout tone="danger">{errorMessage(failure, 'Could not update the webhook')}</Callout>}
      {secret && <SecretBanner secret={secret} onDone={() => setSecret(null)} />}

      {token.webhookUrl && (
        <div>
          <p className="mb-1 text-xs font-medium text-ink">Recent deliveries</p>
          {deliveries.isError ? (
            <Callout tone="danger">{errorMessage(deliveries.error, 'Could not load deliveries')}</Callout>
          ) : deliveries.data && deliveries.data.length > 0 ? (
            <ul className="max-h-56 space-y-0.5 overflow-y-auto font-mono text-[11px] text-ink-muted">
              {deliveries.data.map((d) => (
                <li key={d.id} className="flex flex-wrap gap-x-2">
                  <span>{new Date(d.createdAt).toLocaleString()}</span>
                  <span>{d.shortCode ?? (d.issueId ?? d.prototypeId ?? '').slice(0, 8)}</span>
                  <span>→ {d.requestStatus}</span>
                  <span className={DELIVERY_TONE[d.status] ?? ''}>{d.status}</span>
                  <span>attempt {d.attempt}</span>
                  {d.httpStatus !== null && <span>HTTP {d.httpStatus}</span>}
                  {d.error && <span className="text-ink-faint">{d.error}</span>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-ink-faint">
              {deliveries.isLoading ? 'Loading…' : 'No deliveries yet.'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
